import { useRef, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import { checkUsernameTaken, registerAccount } from '../services/RegistrationService';
import ReCAPTCHA from 'react-google-recaptcha';
import { requireAlphaNumeric } from '../utilities/FormUtility';
import {
    validateUsername,
    validatePassword,
    validateConfirmPassword,
    validateEmail,
    validationClass,
} from '../utilities/validators';
import { FormFeedback } from '../components/FormFeedback';
import { PersonPlusFill } from 'react-bootstrap-icons';

function Register() {
    const captchaRef = useRef(null);
    const usernameDebounceRef = useRef(null);

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [email, setEmail] = useState("");

    const [usernameResult, setUsernameResult] = useState(null);
    const [passwordResult, setPasswordResult] = useState(null);
    const [confirmPasswordResult, setConfirmPasswordResult] = useState(null);
    const [emailResult, setEmailResult] = useState(null);

    const [captchaResponse, setCaptchaResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [feedbackType, setFeedbackType] = useState('error');
    const [feedbackMessage, setFeedbackMessage] = useState(null);

    async function handleSubmit(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!captchaResponse) {
            setFeedbackType('error');
            setFeedbackMessage("Please complete the captcha.");
            return;
        }

        const allValid =
            usernameResult?.valid === true &&
            passwordResult?.valid === true &&
            confirmPasswordResult?.valid === true &&
            emailResult?.valid === true;

        if (!allValid) {
            setFeedbackType('error');
            setFeedbackMessage("Please enter valid information.");
            return;
        }

        setLoading(true);
        setFeedbackMessage(null);

        try {
            await registerAccount(username, password, email, captchaResponse);
            setFeedbackType('success');
            setFeedbackMessage("Your account has been successfully registered!");
            setUsername("");
            setPassword("");
            setConfirmPassword("");
            setEmail("");
            setUsernameResult(null);
            setPasswordResult(null);
            setConfirmPasswordResult(null);
            setEmailResult(null);
            setCaptchaResponse(null);
            captchaRef.current?.reset();
        } catch (err) {
            setFeedbackType('error');
            setFeedbackMessage(err.message || "There was an error registering your account, please try again.");
        } finally {
            setLoading(false);
        }
    }

    function handleUsernameChange(e) {
        const value = e.target.value;
        setUsername(value);

        clearTimeout(usernameDebounceRef.current);

        const formatResult = validateUsername(value);
        if (!formatResult || !formatResult.valid) {
            setUsernameResult(formatResult);
            return;
        }

        setUsernameResult(null);
        usernameDebounceRef.current = setTimeout(async () => {
            try {
                const taken = await checkUsernameTaken(value);
                setUsernameResult(
                    taken
                        ? { valid: false, message: "Username is already taken." }
                        : { valid: true, message: "" }
                );
            } catch {
                setUsernameResult({ valid: false, message: "Could not verify username availability." });
            }
        }, 500);
    }

    function handlePasswordChange(e) {
        const value = e.target.value;
        setPassword(value);
        setPasswordResult(validatePassword(value));
        setConfirmPasswordResult(validateConfirmPassword(confirmPassword, value));
    }

    function handleConfirmPasswordChange(e) {
        const value = e.target.value;
        setConfirmPassword(value);
        setConfirmPasswordResult(validateConfirmPassword(value, password));
    }

    function handleEmailChange(e) {
        const value = e.target.value;
        setEmail(value);
        setEmailResult(validateEmail(value));
    }

    return (
        <div className="register-page">
            <div className="register-card">
                <div className="register-card-header">
                    <div className="register-card-icon"><PersonPlusFill /></div>
                    <h2>Create Account</h2>
                    <p>Join Paragon Conquer and start your adventure</p>
                </div>

                <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="username" label="Username" className="mb-1">
                            <Form.Control
                                required
                                type="text"
                                placeholder="Username"
                                value={username}
                                onChange={handleUsernameChange}
                                onKeyPress={requireAlphaNumeric}
                                className={validationClass(usernameResult)}
                            />
                            <Form.Control.Feedback>Username available!</Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {usernameResult?.message}
                            </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="password" label="Password" className="mb-1">
                            <Form.Control
                                required
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={handlePasswordChange}
                                onKeyPress={requireAlphaNumeric}
                                className={validationClass(passwordResult)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {passwordResult?.message}
                            </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="confirmPassword" label="Confirm Password" className="mb-1">
                            <Form.Control
                                required
                                type="password"
                                placeholder="Confirm Password"
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                onKeyPress={requireAlphaNumeric}
                                className={validationClass(confirmPasswordResult)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {confirmPasswordResult?.message}
                            </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="email" label="Email Address" className="mb-1">
                            <Form.Control
                                required
                                type="email"
                                placeholder="Email Address"
                                value={email}
                                onChange={handleEmailChange}
                                className={validationClass(emailResult)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {emailResult?.message}
                            </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>

                    <div className="register-captcha">
                        <ReCAPTCHA
                            ref={captchaRef}
                            sitekey="6LdFauQdAAAAAGyoyHMjIaDeUQCOC7ewif0ENiXI"
                            onChange={setCaptchaResponse}
                            onExpired={() => setCaptchaResponse(null)}
                            onErrored={() => setCaptchaResponse(null)}
                        />
                    </div>

                    <FormFeedback type="loading" message={loading ? "Creating your account…" : null} />
                    <FormFeedback type={feedbackType} message={feedbackMessage} />

                    <Button className="btn-register" type="submit" disabled={loading}>
                        {loading ? "Creating Account…" : "Create Account"}
                    </Button>
                </Form>
            </div>
        </div>
    );
}

export { Register };
